import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CC_CONSTANTS } from '../../assets/constants';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexPlotOptions,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec5Brown,
    colorAec6Gray,
    colorAec7RedLight,
    colorAec8RedDark,
} from 'src/app/shared/all.constants';
import {
    EDF_CUSTOMERS_POVERTY_EVOL_AMOUNT_POWER_REDUCTION_CHART,
    EDF_CUSTOMERS_POVERTY_EVOL_AMOUNTS_ASSIST_CHART,
    EDF_CUSTOMERS_POVERTY_EVOL_BENEFICIARIES_CHART,
    EDF_CUSTOMERS_POVERTY_EVOL_CUTS_UNPAID_CHART,
    EDF_CUSTOMERS_POVERTY_KPIS,
} from '../../assets/keys';

const KEYS = [
    EDF_CUSTOMERS_POVERTY_KPIS,
    EDF_CUSTOMERS_POVERTY_EVOL_BENEFICIARIES_CHART,
    EDF_CUSTOMERS_POVERTY_EVOL_AMOUNTS_ASSIST_CHART,
    EDF_CUSTOMERS_POVERTY_EVOL_AMOUNT_POWER_REDUCTION_CHART,
    EDF_CUSTOMERS_POVERTY_EVOL_CUTS_UNPAID_CHART,
];

interface TrvKpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    beneficiariesNumber?: number;
    beneficiariesNumberEvolution?: number;

    reductionsPowerNumber?: number;
    reductionsPowerNumberEvolution?: number;
}

interface BenefiChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    nbrTpnBeneficiarie?: number;
    nbrCheckEnergy?: number;
    nbrCertificates?: number;
    rateTpn?: number;
    rateCheckEnergy?: number;
    error?: boolean;
}

interface AmountChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    amountAllocatedEdf?: number;
    nbrCustomersEdf?: number;
    error?: boolean;
}

interface ReducChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    nbrPowerReduct?: number;
    nbrPowerWinterReduct?: number;
    ratioPowerReduct?: number;
    error?: boolean;
}
interface CutsChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    nbrCutsUnpaid?: number;
    ratioCutsUnpaid?: number;
    error?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-edf-poverty',
    templateUrl: './edf-poverty.component.html',
    styleUrls: ['./edf-poverty.component.scss'],
})
export class EdfPovertyComponent extends GenericComponent {
    public texts = CC_CONSTANTS.energyPoverty.texts;

    public kpis: Signal<TrvKpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_POVERTY_KPIS),
    ) as Signal<TrvKpiData[]>;

    public plotOptions: ApexPlotOptions = {
        bar: {
            horizontal: false,
            columnWidth: '30%',
        },
    };

    public benefiChartError: boolean = true;
    public benefiChartSeries: ApexAxisChartSeries = [];
    public benefiDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({
            0: this.texts.evolNbrBeneficiariesChart.precision,
            1: this.texts.evolNbrBeneficiariesChart.precision,
            2: this.texts.evolNbrBeneficiariesChart.precision,
            3: this.texts.evolNbrBeneficiariesChart.ratePrecision,
            4: this.texts.evolNbrBeneficiariesChart.ratePrecision,
        }),
    };
    public benefiXaxisCategories: ApexXAxis['categories'];
    public benefiYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.evolNbrBeneficiariesChart.nbrTpnBeneficiariesAxisTitle,
            min: 0,
            title: {
                text: this.texts.evolNbrBeneficiariesChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.evolNbrBeneficiariesChart.nbrTpnBeneficiariesAxisTitle,
            min: 0,
        },
        {
            seriesName: this.texts.evolNbrBeneficiariesChart.nbrTpnBeneficiariesAxisTitle,
            min: 0,
        },
        {
            seriesName: this.texts.evolNbrBeneficiariesChart.rateTpnAxisTitle,
            min: 0,
            opposite: true,
        },
        {
            seriesName: this.texts.evolNbrBeneficiariesChart.rateTpnAxisTitle,
            min: 0,
            opposite: true,
        },
    ];
    public benefiChartData: Signal<BenefiChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_POVERTY_EVOL_BENEFICIARIES_CHART),
    ) as Signal<BenefiChartData[]>;
    public benefiCustomTooltip: ApexTooltip['custom'];

    public amountChartError: boolean = true;
    public amountChartSeries: ApexAxisChartSeries = [];
    public amountDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
    };
    public amountXaxisCategories: ApexXAxis['categories'];
    public amountYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.evolAmountFileAssistedChart.amountAllocatedEdfAxisTitle,
            min: 0,
            title: {
                text: this.texts.evolAmountFileAssistedChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.evolAmountFileAssistedChart.nbrCustomersEdfAxisTitle,
            min: 0,
            opposite: true,
            title: {
                text: this.texts.evolAmountFileAssistedChart.yaxisTitleRight,
            },
        },
    ];
    public amountChartData: Signal<AmountChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_POVERTY_EVOL_AMOUNTS_ASSIST_CHART),
    ) as Signal<AmountChartData[]>;

    public reducChartError: boolean = true;
    public reducChartSeries: ApexAxisChartSeries = [];
    public reducDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null, 2: null }),
    };
    public reducXaxisCategories: ApexXAxis['categories'];
    public reducYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.evolReducPowerChart.nbrPowerReductAxisTitle,
            min: 0,
            title: {
                text: this.texts.evolReducPowerChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.evolReducPowerChart.nbrPowerReductAxisTitle,
            min: 0,
        },
        {
            seriesName: this.texts.evolReducPowerChart.ratioPowerReductAxisTitle,
            min: 0,
            opposite: true,
            title: {
                text: this.texts.evolReducPowerChart.yaxisTitleRight,
            },
        },
    ];
    public reducChartData: Signal<ReducChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(
            EDF_CUSTOMERS_POVERTY_EVOL_AMOUNT_POWER_REDUCTION_CHART,
        ),
    ) as Signal<ReducChartData[]>;
    public reducCustomTooltip: ApexTooltip['custom'];

    public cutsChartError: boolean = true;
    public cutsChartSeries: ApexAxisChartSeries = [];
    public cutsDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null }),
    };
    public cutsXaxisCategories: ApexXAxis['categories'];
    public cutsYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.evolCutsSupplierChart.nbrCutsUnpaidAxisTitle,
            min: 0,
            title: {
                text: this.texts.evolCutsSupplierChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.evolCutsSupplierChart.ratioCutsUnpaidAxisTitle,
            min: 0,
            opposite: true,
            title: {
                text: this.texts.evolCutsSupplierChart.yaxisTitleRight,
            },
        },
    ];
    public cutsChartData: Signal<CutsChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_POVERTY_EVOL_CUTS_UNPAID_CHART),
    ) as Signal<CutsChartData[]>;
    public cutsCustomTooltip: ApexTooltip['custom'];

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const benefiDataList = this.benefiChartData();
            this.benefiChartError = this.checkForError(benefiDataList, [
                'xYear',
                'nbrTpnBeneficiarie',
                'nbrCheckEnergy',
                'nbrCertificates',
                'rateTpn',
                'rateCheckEnergy',
            ]);

            if (!this.benefiChartError) {
                this.benefiXaxisCategories = benefiDataList.map((data) => data.xYear);
                this.benefiChartSeries = [
                    {
                        name: this.texts.evolNbrBeneficiariesChart.nbrTpnBeneficiariesAxisTitle,
                        type: 'column',
                        data: benefiDataList.map((data) => data.nbrTpnBeneficiarie!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.evolNbrBeneficiariesChart.nbrCheckEnergyAxisTitle,
                        type: 'column',
                        data: benefiDataList.map((data) => data.nbrCheckEnergy!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.evolNbrBeneficiariesChart.nbrCertificatesAxisTitle,
                        type: 'column',
                        data: benefiDataList.map((data) => data.nbrCertificates!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.evolNbrBeneficiariesChart.rateTpnAxisTitle,
                        type: 'line',
                        data: benefiDataList.map((data) => data.rateTpn!),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.evolNbrBeneficiariesChart.rateCheckEnergyAxisTitle,
                        type: 'line',
                        data: benefiDataList.map((data) => data.rateCheckEnergy!),
                        color: colorAec2BlueDark,
                    },
                ];

                this.benefiDataLabels.enabledOnSeries = [0, 1, 2, 3, 4];
            }

            const amountDataList = this.amountChartData();
            this.amountChartError = this.checkForError(amountDataList, [
                'xYear',
                'amountAllocatedEdf',
                'nbrCustomersEdf',
            ]);

            if (!this.amountChartError) {
                this.amountXaxisCategories = amountDataList.map((data) => data.xYear);
                this.amountChartSeries = [
                    {
                        name: this.texts.evolAmountFileAssistedChart.amountAllocatedEdfAxisTitle,
                        type: 'column',
                        data: amountDataList.map((data) => data.amountAllocatedEdf!),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.evolAmountFileAssistedChart.nbrCustomersEdfAxisTitle,
                        type: 'line',
                        data: amountDataList.map((data) => data.nbrCustomersEdf!),
                        color: colorAec7RedLight,
                    },
                ];

                this.amountDataLabels.enabledOnSeries = [0, 1];
            }

            const reducDataList = this.reducChartData();
            this.reducChartError = this.checkForError(reducDataList, [
                'xYear',
                'nbrPowerReduct',
                'nbrPowerWinterReduct',
                'ratioPowerReduct',
            ]);

            if (!this.reducChartError) {
                this.reducXaxisCategories = reducDataList.map((data) => data.xYear);
                this.reducChartSeries = [
                    {
                        name: this.texts.evolReducPowerChart.nbrPowerReductAxisTitle,
                        type: 'column',
                        data: reducDataList.map((data) => data.nbrPowerReduct!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.evolReducPowerChart.nbrPowerWinterReductAxisTitle,
                        type: 'column',
                        data: reducDataList.map((data) => data.nbrPowerWinterReduct!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.evolReducPowerChart.ratioPowerReductAxisTitle,
                        type: 'line',
                        data: reducDataList.map((data) => data.ratioPowerReduct!),
                        color: colorAec8RedDark,
                    },
                ];

                this.reducDataLabels.enabledOnSeries = [0, 1, 2];
            }

            const cutsDataList = this.cutsChartData();
            this.cutsChartError = this.checkForError(cutsDataList, [
                'xYear',
                'nbrCutsUnpaid',
                'ratioCutsUnpaid',
            ]);

            if (!this.cutsChartError) {
                this.cutsXaxisCategories = cutsDataList.map((data) => data.xYear);
                this.cutsChartSeries = [
                    {
                        name: this.texts.evolCutsSupplierChart.nbrCutsUnpaidAxisTitle,
                        type: 'column',
                        data: cutsDataList.map((data) => data.nbrCutsUnpaid!),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.evolCutsSupplierChart.ratioCutsUnpaidAxisTitle,
                        type: 'line',
                        data: cutsDataList.map((data) => data.ratioCutsUnpaid!),
                        color: colorAec8RedDark,
                    },
                ];

                this.cutsDataLabels.enabledOnSeries = [0, 1];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.benefiCustomTooltip = this.setBenefiCustomTooltip();
        this.reducCustomTooltip = this.setReducCustomTooltip();
        this.cutsCustomTooltip = this.setCutsCustomTooltip();
    }

    public setBenefiCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.evolNbrBeneficiariesChart.precision;
            const tpnSerie = w.globals.initialSeries[0];
            const {
                name: tpnName,
                color: tpnColor,
                valueStringified: tpnStringified,
            } = this.setCustomTooltipData(precision, tpnSerie, dataPointIndex);

            const energyCheckSerie = w.globals.initialSeries[1];
            const {
                name: energyCheckName,
                color: energyCheckColor,
                valueStringified: energyCheckStringified,
            } = this.setCustomTooltipData(precision, energyCheckSerie, dataPointIndex);

            const certificatesSerie = w.globals.initialSeries[2];
            const {
                name: certificatesName,
                color: certificatesColor,
                valueStringified: certificatesStringified,
            } = this.setCustomTooltipData(precision, certificatesSerie, dataPointIndex);

            const ratePrecision = this.texts.evolNbrBeneficiariesChart.ratePrecision;
            const tpnRateSerie = w.globals.initialSeries[3];
            const {
                name: tpnRateName,
                color: tpnRateColor,
                valueStringified: tpnRateStringified,
            } = this.setCustomTooltipData(ratePrecision, tpnRateSerie, dataPointIndex);

            const energyCheckRateSerie = w.globals.initialSeries[4];
            const {
                name: energyCheckRateName,
                color: energyCheckRateColor,
                valueStringified: energyCheckRateStringified,
            } = this.setCustomTooltipData(ratePrecision, energyCheckRateSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: tpnColor,
                    name: tpnName,
                    value: tpnStringified,
                },
                {
                    color: energyCheckColor,
                    name: energyCheckName,
                    value: energyCheckStringified,
                },
                {
                    color: certificatesColor,
                    name: certificatesName,
                    value: certificatesStringified,
                },
                {
                    color: tpnRateColor,
                    name: tpnRateName,
                    value: tpnRateStringified,
                },
                {
                    color: energyCheckRateColor,
                    name: energyCheckRateName,
                    value: energyCheckRateStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    public setReducCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.evolReducPowerChart.precision;
            const powerReductSerie = w.globals.initialSeries[0];
            const {
                name: powerReductName,
                color: powerReductColor,
                valueStringified: powerReductStringified,
            } = this.setCustomTooltipData(precision, powerReductSerie, dataPointIndex);

            const powerWinterReductSerie = w.globals.initialSeries[1];
            const {
                name: powerWinterReductName,
                color: powerWinterReductColor,
                valueStringified: powerWinterReductStringified,
            } = this.setCustomTooltipData(precision, powerWinterReductSerie, dataPointIndex);

            const ratePrecision = this.texts.evolReducPowerChart.ratePrecision;
            const cutsSupplierSerie = w.globals.initialSeries[2];
            const {
                name: cutsSupplierName,
                color: cutsSupplierColor,
                valueStringified: cutsSupplierStringified,
            } = this.setCustomTooltipData(ratePrecision, cutsSupplierSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: powerReductColor,
                    name: powerReductName,
                    value: powerReductStringified,
                },
                {
                    color: powerWinterReductColor,
                    name: powerWinterReductName,
                    value: powerWinterReductStringified,
                },
                {
                    color: cutsSupplierColor,
                    name: cutsSupplierName,
                    value: cutsSupplierStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    public setCutsCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.evolCutsSupplierChart.precision;
            const cutsUnpaidSerie = w.globals.initialSeries[0];
            const {
                name: cutsUnpaidName,
                color: cutsUnpaidColor,
                valueStringified: cutsUnpaidStringified,
            } = this.setCustomTooltipData(precision, cutsUnpaidSerie, dataPointIndex);

            const ratePrecision = this.texts.evolCutsSupplierChart.ratePrecision;
            const ratioCutsUnpaidSerie = w.globals.initialSeries[1];
            const {
                name: ratioCutsUnpaidName,
                color: ratioCutsUnpaidColor,
                valueStringified: ratioCutsUnpaidStringified,
            } = this.setCustomTooltipData(ratePrecision, ratioCutsUnpaidSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: cutsUnpaidColor,
                    name: cutsUnpaidName,
                    value: cutsUnpaidStringified,
                },
                {
                    color: ratioCutsUnpaidColor,
                    name: ratioCutsUnpaidName,
                    value: ratioCutsUnpaidStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }
}
